import React, {
  ChangeEvent,
  FC,
  FormEvent,
} from 'react';
import Modal from 'react-modal';
import { BLOCKS } from '@contentful/rich-text-types';
import find from 'lodash/find';

import { Text } from 'src/__generated__/graphqlTypes';
import { CommonRichText, DataButton } from 'components/Shared/SharedComponents';
import styles from 'components/Sections/ContactUsGetInTouch/FeedbackForm/FeedbackForm.module.scss';
import classes from 'src/styles/_commonClasses.module.scss';
import FeedbackFormSelect from 'components/Sections/ContactUsGetInTouch/FeedbackForm/FeedbackFormSelect';
import {
  IdNameType,
  RichTextParsersConfig,
  ValidationAndValue,
} from 'types/objectTypes';
import { FeedbackFormStateValue } from 'constants/enums';
import { commonEmailRegexp, commonPhoneRegexp } from 'lib/regexp.service';
import { FeedbackFormTextarea, FeedbackFormInput } from 'components/Sections/ContactUsGetInTouch/FeedbackForm/FeedbackFormInput';
import { TEXTAREA_MAX_LENGTH } from 'constants/constants';
import {
  FeedbackFormInterfaceProps,
  ZendeskModalInterface,
  ErrorHandler,
} from 'components/Sections/ContactUsGetInTouch/FeedbackForm/declarations';

const parsersConfig: RichTextParsersConfig = {
  [BLOCKS.PARAGRAPH]: {
    classNames: styles.modalParagraph,
  },
};

/* Nested component */
const ZendeskModal: FC<ZendeskModalInterface> = ({
  modal,
  isModalVisible = false,
  closeModal,
}) => {
  if (!modal) {
    return null;
  }

  const { link, title, content } = modal;

  return (
    <Modal
      isOpen={isModalVisible}
      className={styles.modal}
      overlayClassName={styles.modalBackground}
      shouldCloseOnOverlayClick
      bodyOpenClassName={classes.overflowHidden}
      onRequestClose={closeModal}
    >
      <span className={styles.modalTitle}>{title}</span>
      {content && (
        <CommonRichText
          content={content}
          parsersConfig={parsersConfig}
        />
      )}
      {link && (
        <DataButton
          link={link}
          className={styles.modalButton}
          onClick={closeModal}
        >
          {link.title}
        </DataButton>
      )}
    </Modal>
  );
};

const findErrorIfNeeded = (
  inputData: ValidationAndValue<IdNameType<string> | string>,
  isValid: boolean | null,
  isValuePresented?: boolean,
) => {
  let { error } = inputData;
  const { alerts, value } = inputData;

  if (!isValid) {
    const condition = (isValuePresented !== undefined) ? isValuePresented : !value;

    if (condition) {
      error = alerts.find((alert) => alert.slug === 'required');
    } else {
      error = alerts.find((alert) => alert.slug === 'invalid');
    }
  }

  return { ...inputData, error };
};

/* Main component */
const FeedbackForm: FC<FeedbackFormInterfaceProps> = ({
  requestTypeOptions,
  title,
  button,
  modal,
  isModalVisible,
  countries,
  inputValues,
  setInputValues,
  searchInCountries,
  closeModal,
  handleSubmitForm,
  fetchCountries,
}) => {
  const getValidation = (value: string | IdNameType<string>, pattern?: RegExp | string | null) => {
    if (typeof value === 'string') {
      return Boolean(value)
        && ((Boolean(pattern) && Boolean((value as string).toLowerCase().match(pattern!)))
          || !pattern);
    }

    return (Boolean(value.id) && Boolean(value.name))
      && ((Boolean(pattern) && Boolean(value.name.toLowerCase().match(pattern!)))
        || !pattern);
  };

  const getCommonValidationAndError = (
    item: ValidationAndValue<string | IdNameType<string>>,
    pattern?: RegExp | string | null,
    itemValue?: string | IdNameType<string>,
  ) => {
    const { value, isValid } = item;
    const currentValue = itemValue || value;
    const validation = getValidation(currentValue, pattern);
    const isValidOrNull = (isValid !== null) ? validation : null;
    const { error } = findErrorIfNeeded(
      item,
      isValidOrNull,
      (typeof currentValue === 'string') ? undefined : !validation,
    );

    return { error, validation, isValid: isValidOrNull };
  };

  const getCountryValidationAndError = (
    item: ValidationAndValue<string | IdNameType<string>>,
    pattern?: RegExp | string | null,
    itemValue?: string | IdNameType<string>,
  ) => {
    const { value, isValid } = item;
    const currentValue = itemValue || value;
    const country = find(countries, (country) => country.text === currentValue);
    const validation = getValidation(country?.text || '', pattern);
    const isValidOrNull = (isValid !== null) ? validation : null;
    const { error } = findErrorIfNeeded(item, isValidOrNull, !validation);

    return { error, validation, isValid: isValidOrNull };
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    errorHandler: ErrorHandler = getCommonValidationAndError,
    pattern?: RegExp | string | null,
  ) => {
    const { name, value } = e?.target || {};
    const inputItem: ValidationAndValue<any> = inputValues[name];
    const { error, isValid, validation } = errorHandler(inputItem, pattern, value);

    const values = {
      ...inputValues,
      [name]: {
        ...inputItem,
        value,
        isValid: (isValid !== null) ? validation : null,
        error,
      },
    };
    setInputValues(values);
  };

  const onBlur = (
    name: FeedbackFormStateValue,
    errorHandler: ErrorHandler = getCommonValidationAndError,
    pattern?: RegExp | string | null,
  ) => setInputValues((prev) => {
    const prevItem = prev[name];
    const { validation, error } = errorHandler(prevItem, pattern);

    return ({
      ...prev,
      [name]: {
        ...prevItem,
        isValid: validation,
        error,
      },
    });
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isInvalid = false;
    Object.entries(inputValues)
      .forEach(([key, inputObject]) => {
        const { isValid, value } = inputObject as ValidationAndValue<string | IdNameType<string>>;

        if (!isValid) {
          isInvalid = true;
          const isValuePresented = !value
            || (!(value as IdNameType<string>).id
              && !(value as IdNameType<string>).name);
          const { error } = findErrorIfNeeded(inputObject, isValid, isValuePresented);

          setInputValues((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              isValid: false,
              error,
            },
          }));
        }
      });

    if (!isInvalid) {
      handleSubmitForm(inputValues);
    }
  };

  const setSelectValue = (
    name: FeedbackFormStateValue,
    value: string | IdNameType<string>,
  ) => {
    setInputValues((inputValues) => ({
      ...inputValues,
      [name]: {
        ...inputValues[name],
        isValid: true,
        value,
      },
    }));
  };

  const setRequestType = (requestType: Text) => setSelectValue(
    FeedbackFormStateValue.REQUEST_TYPE,
    { id: requestType.slug!, name: requestType.text! },
  );

  const setCountry = (country: Text) => setSelectValue(
    FeedbackFormStateValue.COUNTRY,
    country.text!,
  );

  return (
    <form
      id={styles.form}
      className={styles.form}
      onSubmit={handleSubmit}
    >
      <fieldset
        form={styles.form}
        className={styles.formFieldset}
      >
        <legend className={styles.formFieldsetTitle}>
          {title}
        </legend>
        <FeedbackFormInput
          name={FeedbackFormStateValue.NAME}
          inputData={inputValues.name}
          onChange={(e) => handleInputChange(e)}
          onBlur={() => onBlur(FeedbackFormStateValue.NAME)}
        />
        <FeedbackFormInput
          name={FeedbackFormStateValue.EMAIL}
          inputData={inputValues.email}
          onChange={(e) => handleInputChange(
            e,
            getCommonValidationAndError,
            commonEmailRegexp,
          )}
          onBlur={() => onBlur(
            FeedbackFormStateValue.EMAIL,
            getCommonValidationAndError,
            commonEmailRegexp,
          )}
        />
        <FeedbackFormInput
          name={FeedbackFormStateValue.PHONE_NUMBER}
          inputData={inputValues.phoneNumber}
          onChange={(e) => handleInputChange(
            e,
            getCommonValidationAndError,
            commonPhoneRegexp,
          )}
          onBlur={() => onBlur(
            FeedbackFormStateValue.PHONE_NUMBER,
            getCommonValidationAndError,
            commonPhoneRegexp,
          )}
          className={styles.formFieldsetDivInputSmall}
        />
        <FeedbackFormSelect
          isInput
          name={FeedbackFormStateValue.COUNTRY}
          title={inputValues.country.placeholder!}
          selectOptions={countries}
          inputData={inputValues.country}
          setInputValue={setCountry}
          onBlur={() => onBlur(FeedbackFormStateValue.COUNTRY, getCountryValidationAndError)}
          onClick={fetchCountries}
          onChange={(e) => {
            handleInputChange(e, getCountryValidationAndError);
            searchInCountries(e.target.value);
          }}
          className={styles.formFieldsetDivInputSmall}
        />
        <FeedbackFormSelect
          title={inputValues.requestType.placeholder!}
          selectOptions={requestTypeOptions!}
          inputData={inputValues.requestType}
          onBlur={() => onBlur(FeedbackFormStateValue.REQUEST_TYPE)}
          setInputValue={setRequestType}
        />
        <FeedbackFormTextarea
          name={FeedbackFormStateValue.MESSAGE}
          inputData={inputValues.message}
          onChange={(e) => handleInputChange(e)}
          onBlur={() => onBlur(FeedbackFormStateValue.MESSAGE)}
          className={styles.formFieldsetDivInputBig}
          maxLength={TEXTAREA_MAX_LENGTH}
        />
      </fieldset>
      {button && (
        <DataButton
          type="submit"
          form={styles.form}
          className={styles.formSubmit}
        >
          {button.title}
        </DataButton>
      )}
      <ZendeskModal
        modal={modal}
        isModalVisible={isModalVisible}
        closeModal={closeModal}
      />
    </form>
  );
};

export default FeedbackForm;
